import React, { useEffect, Fragment, useRef, useState, useCallback } from 'react';
import setFieldData from 'final-form-set-field-data';
import cn from 'classnames';
import PropTypes from 'prop-types';
import utils from '@ehi/global-marketing-interface/utils';
import { dtm } from 'utils/analytics';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { scrollPage, isContainedInSixColumnGrid, isAtStartOfStandAloneBookingWidgetPage } from 'utils/dom';
import { profileProgramMatchPRP } from 'utils/profile';
import useClickOutside from 'hooks/useClickOutside';
import { getIntegerNumber } from 'utils/number';
import { getFormattedContractRestrictionDates } from 'utils/reservation';
import { formatDateTimeStampAndReturnDateTimeObj } from 'utils/date';
import { objectHasOwnProperty } from 'utils/object';
import { deleteSessionStorageKey } from 'utils/localStorage';
import { getSearchFilterValue } from 'utils/vehicles';
import { showEveryDayLowRatesBanner } from 'utils/banners';
import { GMI_SERVICE_PATHS, RESERVATIONS, LOCATIONS, ANALYTICS, WINDOW_OBJECT_KEYS, SESSION_STORAGE } from 'constants';
import Form from 'components/Form/Form';
import Button from 'components/Button';
import Anchor from 'components/Anchor';
import GenericNotification from 'components/GenericNotification';
import PRPAuthAddProgramModal from 'components/modals/PRPAuthAddProgramModal';
import PRPAuthConflictModal from 'components/modals/PRPAuthConflictModal';
import LocationSearch from 'components/LocationSearch';
import DriversAge from 'components/DriversAge';
import PartnerRewardsFields from 'components/PartnerRewardsFields';
import DiscountCodeVehicleClass from 'components/DiscountCodeVehicleClass';
import GuidedFormIndicator from 'components/Form/GuidedFormIndicator';
import ServiceErrors from 'components/ServiceErrors';
import FormErrors from 'components/FormErrors';
import RemoveProductCodeModal from 'components/modals/RemoveProductCodeModal';
import VehicleClassFilterModal from 'components/modals/VehicleClassFilterModal';
import CidConflictModal from 'components/modals/CidConflictModal/CidConflictModal';
import PrerateAdditionalInfoModal from 'components/modals/PrerateAdditionalInfoModal';
import FrequentTravelerRecoveryFeeModal from 'components/modals/FrequentTravelerRecoveryFeeModal';

import LocationDateTimeFieldGroup from './LocationDateTimeFieldGroup';
import LocationDateTimeFormSpy from './LocationDateTimeFieldGroup/LocationDateTimeFormSpy';

const {
  EXCHANGE_TYPE_PICKUP,
  EXCHANGE_TYPE_RETURN,
  LAST_MINUTE_SPECIALS,
  PLAN_AHEAD_SPECIALS,
  IS_RES_INITIATED_COUPON_CID,
} = RESERVATIONS;

const validateReturnLocation = (value, returnLocation) =>
  (value && value?.type) || (value && returnLocation) ? null : i18n('bw_return_location_submit_error');

const validate = (values) => {
  const errors = {};

  if (!values.pickupLocation) {
    errors.pickupLocation = i18n('bw_pickup_location_submit_error');
  }
  if (!values.pickupDate) {
    errors.pickupDate = i18n('bw_pickup_date_submit_error');
  }
  if (!values.pickupTime) {
    errors.pickupTime = i18n('bw_pickup_time_submit_error');
  }
  if (!values.returnDate) {
    errors.returnDate = i18n('bw_return_date_submit_error');
  }
  if (!values.returnTime) {
    errors.returnTime = i18n('bw_return_time_submit_error');
  }
  if (!values.driversAge) {
    errors.driversAge = i18n('bw_drivers_age_submit_error');
  }

  return errors;
};

/**
 * BookingWidget component for initiating a reservation from various parts of the site
 *
 * @param {object}    props - React Props
 * @param {string}    props.contract_number - comes from gmi.ui.reservations.initiate.contract_number
 * @param {array}     props.coupons - comes from gmi.ui.reservations.initiate.coupons
 * @param {array}     props.couponDetails - Coupons details info from BW authoring
 * @param {function}  props.handleInitiate - GMI function for initializing a reservation based on props and values in gmi.ui.reservations.initiate
 * @param {boolean}   props.one_way_rental - comes from gmi.ui.reservations.initiate.one_way_rental
 * @param {object}    props.pickup_location - comes from gmi.ui.reservations.initiate.pickup_location
 * @param {string}    props.pickup_location_id - comes from gmi.ui.reservations.initiate.pickup_location_id
 * @param {object}    props.pickupLocationFromProps - comes from aem component authoring - prefilled location object
 * @param {string|number}  props.renter_age - comes from gmi.ui.reservations.initiate.renter_age
 * @param {object}    props.return_location - comes from gmi.ui.reservations.initiate.return_location
 * @param {string}    props.return_location_id - comes from gmi.ui.reservations.initiate.return_location_id
 * @param {function}  props.setCoupon - store coupon in gmi.ui.reservations.initiate
 * @param {function}  props.setContractNumber - store contract in gmi.ui.reservations.initiate
 * @param {function}  props.setLocation - store location in gmi.ui.reservations.initiate, by exchange type
 * @param {boolean}   props.isCollapsed - identifies if BW should render in collapsed state
 * @param {object}    props.breakpoint - identifies mobile/tablet/desktop breakpoint status
 * @param {object}    props.contractDetails - Authorable Contract (CID) details object
 * @param {object}    props.partnerRewardProgram - Authorable PRP object loaded for PRP pages
 * @param {boolean}   props.enablePartnerRewards - Authorable PRP flag to show/hide PRP fields
 * @param {function}  props.setPartnerRewardProgram - Action to set PRP code and name to GMI initiate request
 * @param {function}  props.setPartnerRewardProgramMemberId - Action to set PRP member ID to GMI initiate request
 * @param {boolean}   props.isTrueModify - identifies True Modify status:
 * @param {boolean}   props.isDeeplink - flag to check if it is a deeplink
 * @param {object}    props.profileData - object with retrieved data from user profile state
 * @param {number}    props.profileData.age - user profile age
 * @param {boolean}   props.disableCouponCode - flag set by authoring to disable Coupon Code field
 * @param {boolean}   props.contractCollapsed - flag set by authoring to collapse/expand Discount Codes fields by default
 * @param {boolean}   props.collapsed - flag set by authoring to collapse the entire Booking Widget
 * @param {string}    props.bundleProfile - default bundle authored from AEM
 * @param {function}  props.setBundleProfile - GMI function to set a bundle to the initiate reservation
 * @param {boolean}   props.returnLocationCollapsed - flag set by authoring to collapse/expand the return location input
 * @return {JSX}      BookingWidget component
 */

const BookingWidget = ({
  pickupLocationFromProps,
  pickup_location,
  reservationObject,
  setContractNumber,
  setCoupon,
  setLocation,
  unsetLocation,
  setPartnerRewardProgram,
  driversAgeInitialValue,
  contractDetails,
  couponDetails,
  partnerRewardProgram,
  profileData,
  productCode,
  showProductCode,
  clearInitialVehicleClassFilterData,
  discountCodesExpanded,
  collapsed,
  isTrueModify,
  contractCollapsed,
  one_way_rental,
  returnLocationCollapsed,
  vehicleSearchFilters,
  enablePartnerRewards,
  disableCouponCode,
  handleInitiate,
  showFrequentTravelerRecoveryFeeModal,
  showUpdateProfileWithPRPModal,
  showAuthConflictPRPModal,
  setDiscountCodeExpanded,
  return_location,
  uiInitiateRequestalues,
  breakpoint,
  pickup_location_id,
  return_location_id,
  pickupDate,
  pickupDateValue,
  pickupEligibility,
  pickupTime,
  returnDate,
  returnDateValue,
  returnEligibility,
  returnTime,
  setPartnerRewardProgramMemberId,
  openRemoveProductCodeModal,
  isDeeplink,
  showVehicleClassFilterModal,
  reservationObjectInitialValues,
  isInResBookingWidget,
  setIsGmiUIOneWayRental,
  clearDate,
  setDate,
  bundleProfile,
  setBundleProfile,
  getAgeRangeDefaults,
  initiateRequestDetails,
  reservationInitiateError,
  isBranchWidget,
  didProgressBarLocationInitiate,
  prefilled,
  setAbandonedRes,
  rentalStateConfirmed,
  removeInitiateResObject,
  getAgeRangeDefaultsByLocation,
  setTime,
}) => {
  // This flag checks if the BW have CID or Coupon code from AEM authoring
  const isDiscountCodeAuthored =
    !!contractDetails?.contractDetails || utils.isArrayNotEmpty(couponDetails?.couponDetails) || discountCodesExpanded;

  // booking widget default/initial state  values

  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [isDiscountCollapsed, setIsDiscountCollapsed] = useState(
    isDiscountCodeAuthored || isTrueModify ? false : contractCollapsed
  );
  const [isOneWay, setIsOneWay] = useState(one_way_rental || !returnLocationCollapsed);
  const [disableGuidedFlow, setDisableGuidedFlow] = useState(false);
  const [initialValues, setInitialValues] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [isSixColumnGrid, setIsSixColumnGrid] = useState(false);
  const [hasClearedSession, setHasClearedSession] = useState(false);
  const [restrictedDate, setRestrictedDate] = useState(false);
  const [isLmOrPlanAheadDiscountRemoved, setIsLmOrPlanAheadDiscountRemoved] = useState(false);
  const [pickupLocationRemoved, setPickupLocationRemoved] = useState(false);
  const [returnLocationRemoved, setReturnLocationRemoved] = useState(false);
  const [initialSearchByVehicles, setInitialSearchByVehicles] = useState(getSearchFilterValue(vehicleSearchFilters));
  const [reservationSessionLDTUpdated, setReservationSessionLDTUpdated] = useState(false);
  const [isDriverAgeUpdated, setIsDriverAgeUpdated] = useState(false);
  const [displayingPreservedSessionData, setDisplayingPreservedSessionData] = useState(false);
  const [initialValuesUpdated, setInitialValuesUpdated] = useState(false);

  let returnLocationForHydration = null; // Stores return location for toggle rehydration

  const clickOutsideRef = useRef();

  const authoredContractSubType = contractDetails?.contractDetails?.contractSubType;
  const isLastMinuteSpecials = authoredContractSubType === LAST_MINUTE_SPECIALS;
  const isPlanAheadSpecials = authoredContractSubType === PLAN_AHEAD_SPECIALS;
  const authoredContractRestrictions = contractDetails?.contractDetails?.restrictions;
  const authoredFormattedContractRestrictions =
    authoredContractRestrictions &&
    getFormattedContractRestrictionDates(authoredContractRestrictions, isPlanAheadSpecials);

  const resetInitiateResState =
    !isTrueModify &&
    rentalStateConfirmed &&
    pickup_location_id &&
    !hasClearedSession &&
    isAtStartOfStandAloneBookingWidgetPage;

  // check for reservation stored in session and then set initial pre fill LDT information
  const hasReservationSessionData =
    !isInResBookingWidget &&
    !isTrueModify &&
    !utils.isObjectEmpty(reservationObjectInitialValues) &&
    !reservationSessionLDTUpdated;

  const driversAgeIntegerValue = getIntegerNumber(initialValues?.driversAge);
  const skipAnalyticsForPickAndReturnDateTime = sessionStorage.getItem(
    ANALYTICS.SKIP_ANALYTICS_FOR_SAVED_RESERVATION_SESSION_LDT_VALUES
  );

  // clearing sessionStorage values once the initiateRequestValues for pickup_time and return_time got updated.
  const clearSessionStorage =
    !isTrueModify &&
    reservationSessionLDTUpdated &&
    uiInitiateRequestalues.pickup_time &&
    uiInitiateRequestalues.return_time &&
    skipAnalyticsForPickAndReturnDateTime;

  const { selectedValues } = vehicleSearchFilters;

  const newInitialValues = { ...initialValues };
  let changedInitialValues = false;

  if (profileData?.age && initialValues?.driversAge !== profileData?.age) {
    changedInitialValues = true;
    newInitialValues.driversAge = profileData?.age;
  }
  if (
    !profileData?.age &&
    !isInResBookingWidget &&
    !isTrueModify &&
    driversAgeIntegerValue > 0 &&
    initialValues?.driversAge &&
    reservationObjectInitialValues.renter_age &&
    driversAgeInitialValue &&
    reservationObjectInitialValues.renter_age <= driversAgeInitialValue &&
    initialValues?.driversAge !== reservationObjectInitialValues.renter_age
  ) {
    changedInitialValues = true;
    newInitialValues.driversAge = reservationObjectInitialValues.renter_age;
  }

  const fetchDefaultAgeRentals = (location) => {
    // set ageRental defaults value on the booking widget when user changes the location
    location?.address &&
      Promise.resolve(getAgeRangeDefaultsByLocation(location.address.country_code)).then((res) => {
        if (!res.messages) {
          // user selected age
          let userSelection = initialValues?.driversAge;
          let userSelectedAge = sessionStorage.getItem(SESSION_STORAGE.USER_AGE_SELECTED);

          if (
            !isTrueModify &&
            !isInResBookingWidget &&
            reservationObjectInitialValues?.renter_age &&
            reservationSessionLDTUpdated
          ) {
            userSelection = reservationObjectInitialValues?.renter_age;
            userSelectedAge = true;
          }
          if (!reservationObjectInitialValues?.renter_age && !reservationSessionLDTUpdated) {
            deleteSessionStorageKey(SESSION_STORAGE.USER_AGE_SELECTED);
            userSelectedAge = false;
          }
          if (isInResBookingWidget) {
            userSelectedAge = false;
          }

          // each location has a default selected age
          const defaultSelection = res.find((age) => age.selected);
          // sometimes user age is not present in location response, so just checking
          const userSelectionExists = res.some((age) => age?.value === userSelection);

          // if user selected age exists we use it, otherwise we use the default age from location
          const selectedAge = userSelectionExists && userSelectedAge ? userSelection : defaultSelection?.value;

          if (selectedAge) {
            const modifyInitialValues = { ...initialValues };
            modifyInitialValues.driversAge = selectedAge;
            modifyInitialValues.pickupLocation = location;
            setInitialValues({ ...modifyInitialValues });
            setIsDriverAgeUpdated(true);
          }
        }
      });
  };

  /**
   * handleSetLocation - Updates redux store with provided location object - used both for pickup and return locations
   *
   * @param {String} exchangeType pickup or return location - defaults to pickup for round trip case
   * @param {Object} location Location object from GBO
   */
  const handleSetLocation =
    (exchangeType = EXCHANGE_TYPE_PICKUP) =>
    (location) => {
      if (isOneWay && exchangeType === EXCHANGE_TYPE_RETURN) {
        returnLocationForHydration = location;
      }
      if (!isOneWay) {
        setIsDriverAgeUpdated(!isDriverAgeUpdated);
        setLocation(location);

        fetchDefaultAgeRentals(location);
      } else {
        setLocation(location, exchangeType);
        // set one_way_rental true , if isOneWay state is true boolean value and one_way_rental is undefined /false;
        if (!one_way_rental && isOneWay) {
          setIsGmiUIOneWayRental(isOneWay);
        }
      }
      setIsCollapsed(false);
    };

  const initLocation = useCallback(() => {
    initialValues.pickupLocation && handleSetLocation(EXCHANGE_TYPE_PICKUP)(initialValues.pickupLocation);
    one_way_rental &&
      initialValues.returnLocation &&
      handleSetLocation(EXCHANGE_TYPE_RETURN)(initialValues.returnLocation);
  }, [initialValuesUpdated]);

  const toggleDiscountCollapsed = (toggle) => {
    setIsDiscountCollapsed(toggle);
  };

  const getSplitTime = (pickup_return_time) => pickup_return_time?.split('T') || [];

  // Show everday low rate banner according to the CID restrictions
  const shouldDisplayEveryDayLowRatesBanner = (type, date) => {
    const restrictedDateValue = showEveryDayLowRatesBanner(
      type,
      date,
      pickupDate,
      returnDate,
      authoredFormattedContractRestrictions || initialValues?.formattedContractRestrictions
    );
    setRestrictedDate(restrictedDateValue);
  };

  const handleSetDate = (exchangeType) => (date) => {
    // The time stamp is already coming in as an abstracted (timeless) string, this transformation is just to
    // format that string correctly... `Tue May 12 2020 00:00:00 GMT+0200 (Central European Summer Time)` => `2020-05-12`
    const timeStamp = utils.getDateTimeObjFromTs(date).format().slice(0, 10);

    let expectedExchangeType = exchangeType;

    if (exchangeType === EXCHANGE_TYPE_PICKUP && utils.getDateTimeObjFromTs(returnDate).isBefore(timeStamp)) {
      clearDate(EXCHANGE_TYPE_RETURN);
    }

    if (exchangeType === EXCHANGE_TYPE_RETURN && utils.getDateTimeObjFromTs(pickupDate).isAfter(timeStamp)) {
      expectedExchangeType = EXCHANGE_TYPE_PICKUP;
      returnDate && clearDate(EXCHANGE_TYPE_RETURN);
    }

    (authoredContractRestrictions || initialValues?.contractRestrictions) &&
      shouldDisplayEveryDayLowRatesBanner(exchangeType, formatDateTimeStampAndReturnDateTimeObj(date));

    setDate(timeStamp, expectedExchangeType);
  };

  const handleSetTime = (exchangeType) => (time) => {
    setTime(time, exchangeType);
  };

  /**
   * onToggleOneWay - Toggles one way and updates return location object in store
   */
  const onToggleOneWay = () => {
    // When toggling isOneWay we need to unset return location which might've been set to match pickup earlier
    unsetLocation(EXCHANGE_TYPE_RETURN);
    // When removing different return location have to reset return location in store to match pickup
    if (isOneWay && pickup_location) {
      returnLocationForHydration = return_location; // store the return location
      setLocation(pickup_location, EXCHANGE_TYPE_RETURN);
    }
    if (!isOneWay && returnLocationForHydration) {
      setLocation(returnLocationForHydration, EXCHANGE_TYPE_RETURN);
    }
    setIsGmiUIOneWayRental(!isOneWay);
    setIsOneWay(!isOneWay);
    setIsCollapsed(false);
  };

  /**
   * handleDisabledGuidedFlowState - enables the guided flow if it is clicked outside the booking widget
   */

  const handleDisabledGuidedFlowState = () => {
    if (disableGuidedFlow) {
      setDisableGuidedFlow(false);
    }
  };

  /**
   * handleUnsetLocation - Removes provided location from global store
   *
   * @param {String} exchangeType Type of location being removed
   */
  const handleUnsetLocation =
    (exchangeType = EXCHANGE_TYPE_PICKUP) =>
    () => {
      if (isOneWay && exchangeType === EXCHANGE_TYPE_RETURN) {
        returnLocationForHydration = null;
      }
      if (!isOneWay) {
        unsetLocation(EXCHANGE_TYPE_RETURN);
      }
      unsetLocation(exchangeType);

      exchangeType === EXCHANGE_TYPE_PICKUP && setPickupLocationRemoved(true);
      exchangeType === EXCHANGE_TYPE_RETURN && setReturnLocationRemoved(true);
      handleDisabledGuidedFlowState();
    };

  const handleClickOutside = () => {
    setDisableGuidedFlow(!disableGuidedFlow);
  };

  /**
   * Handle Discount Code Expand Callback
   */
  const handleDiscountCodeExpand = (isExpanded) => {
    setIsDiscountCollapsed(!isExpanded);
  };

  /**
   * Handle LMS or Plan Ahead Discount is removed from LMS/PAS page
   */
  const handleLmOrPlanAheadDiscountRemoved = (isRemoved) => {
    setIsLmOrPlanAheadDiscountRemoved(isRemoved);
  };

  const applyReservation = (reservation = {}, initialValuesPassedIn = {}) => {
    const foundReservation = !utils.isObjectEmpty(reservation);

    const { pickup_time, return_time, renter_age, coupons, contract_details } = reservation;

    let resInitialValues = { ...initialValuesPassedIn };

    // Contract number should be blank if it is the alamo insiders loyalty contract
    const shouldPopulateContractNumber = reservation?.contract_number && !contract_details?.default_loyalty_contract;
    const contactNumberInitialValue = shouldPopulateContractNumber ? reservation?.contract_number : '';
    const initialContractNumberBasedOnPASOrLMS =
      objectHasOwnProperty(reservation, 'isCurrentPathSameasPreviousPath') &&
      !reservation.isCurrentPathSameasPreviousPath
        ? resInitialValues?.contractNumber
        : contactNumberInitialValue;

    // must wait for async age range response, or resolve false
    Promise.resolve(
      !!(foundReservation && reservation?.pickup_location.address) &&
        getAgeRangeDefaults(reservation?.pickup_location.address.country_code)
    ).then(() => {
      if (foundReservation) {
        // pickup/return datetime is different than from GBO, should update with GBO info
        // reset gmi > ui, to match what's in gbo
        const [pickupDateVal, pickupTimeVal] =
          isTrueModify && reservationInitiateError.length > 0 && pickup_time !== initiateRequestDetails?.pickup_time
            ? getSplitTime(initiateRequestDetails?.pickup_time)
            : getSplitTime(pickup_time);
        const [returnDateVal, returnTimeVal] =
          isTrueModify && reservationInitiateError.length > 0 && return_time !== initiateRequestDetails?.return_time
            ? getSplitTime(initiateRequestDetails?.return_time)
            : getSplitTime(return_time);

        // Check if contract is Last Mintue Special or Plan Ahead Special
        const initialValuesIsLmOrPlanAheadSpecials =
          contract_details && contract_details?.contract_sub_type === (LAST_MINUTE_SPECIALS || PLAN_AHEAD_SPECIALS);

        // Use for inRes Booking Widget... FE get the information from resInitialValues state
        const initialContractRestriction = contract_details && contract_details?.restrictions;
        const initialValueFormattedContractRestrictions =
          initialContractRestriction &&
          getFormattedContractRestrictionDates(
            initialContractRestriction,
            contract_details?.contract_sub_type === PLAN_AHEAD_SPECIALS
          );

        // didProgressBarLocationInitiate will be set to false on initiate reservation failure and then we use gboPickuplocation
        let pickupLocation = reservation?.pickup_location;
        let returnLocation = reservation?.return_location;

        // reset gmi>ui to match with gbo pick up and return location;

        if (!didProgressBarLocationInitiate && Object.hasOwn(initiateRequestDetails, 'pickup_location')) {
          pickupLocation =
            initiateRequestDetails?.pickup_location?.name !== pickupLocation?.name
              ? initiateRequestDetails?.pickup_location
              : pickupLocation;
          returnLocation =
            initiateRequestDetails?.return_location?.name !== pickupLocation?.name
              ? initiateRequestDetails?.return_location
              : returnLocation;
        }

        // If reservartion widget is branch, replace reservation pickup location with branch location
        // and keep the rest of the reservation session object
        if (isBranchWidget) {
          if (resInitialValues?.pickupLocation && reservation?.pickup_location) {
            pickupLocation = resInitialValues?.pickupLocation;
          }
        }

        resInitialValues = {
          pickupLocation,
          pickupDate: pickupDateVal,
          pickupTime: pickupTimeVal,
          returnLocation,
          returnDate: returnDateVal,
          returnTime: returnTimeVal,
          driversAge: renter_age,
          contractNumber: initialContractNumberBasedOnPASOrLMS,
          contractName: contract_details?.contract_name,
          contractSubType: contract_details?.contract_sub_type,
          contractIsLmOrPlanAheadSpecials: initialValuesIsLmOrPlanAheadSpecials,
          contractRestrictions: contract_details?.restrictions,
          contractDetails: contract_details,
          couponCode: coupons?.[0]?.description || couponDetails?.couponDetails?.[0]?.description || coupons?.[0],
          formattedContractRestrictions: initialValueFormattedContractRestrictions,
          partnerRewardsProgram: partnerRewardProgram?.name,
          partnerRewardsCode: partnerRewardProgram?.code,
        };
      }
      setIsOneWay(isOneWay);
      setDisableGuidedFlow(foundReservation);
      setInitialValues(resInitialValues);
      setIsOneWay(reservation?.one_way_rental || isOneWay);
      setIsDiscountCollapsed(
        !(
          reservation?.contract_number ||
          coupons?.[0] ||
          resInitialValues?.couponCode ||
          isTrueModify ||
          productCode ||
          showProductCode ||
          initialContractNumberBasedOnPASOrLMS
        )
      );

      // Set values on gmi.ui.reservations.initiate
      resInitialValues.contractNumber && setContractNumber(resInitialValues.contractNumber);

      resInitialValues.couponCode &&
        setCoupon(coupons?.[0]?.code || couponDetails?.couponDetails?.[0]?.code || coupons?.[0]);

      resInitialValues.pickupDate && handleSetDate(EXCHANGE_TYPE_PICKUP)(resInitialValues.pickupDate);

      resInitialValues.pickupTime && handleSetTime(EXCHANGE_TYPE_PICKUP)(resInitialValues.pickupTime);

      resInitialValues.returnDate && handleSetDate(EXCHANGE_TYPE_RETURN)(resInitialValues.returnDate);

      resInitialValues.returnTime && handleSetTime(EXCHANGE_TYPE_RETURN)(resInitialValues.returnTime);

      reservation?.one_way_rental && setIsGmiUIOneWayRental(resInitialValues?.one_way_rental);
    });

    setInitialValuesUpdated(true);
  };

  useEffect(() => {
    // Safari uses [back-forward cache] that shows the cached page when the user clicks the back button
    // so, we need to hide the loading overlay when the page is loaded from the cache
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        clearInitialVehicleClassFilterData();
      }
    });

    // If authors configured component with predefined bundle profile
    if (bundleProfile) {
      setBundleProfile(bundleProfile);
    }

    if (productCode || showProductCode) {
      toggleDiscountCollapsed(false);
    }

    const profileProgramDoesntMatchComponentProgram = !profileProgramMatchPRP(profileData, partnerRewardProgram);

    const isSixColumnGridValue = isContainedInSixColumnGrid(clickOutsideRef.current);
    setIsSixColumnGrid(isSixColumnGridValue);

    const haveLocationFromProps = !utils.isObjectEmpty(pickupLocationFromProps) && !pickup_location;
    let modifyInitialValues = { driversAge: driversAgeInitialValue };

    if (haveLocationFromProps) {
      // If authors configured component with preselected location and user has not changed it
      // setLocation action has to be dispatch to update store
      modifyInitialValues = {
        ...modifyInitialValues,
        pickupLocation: pickupLocationFromProps,
        returnLocation: pickupLocationFromProps,
      };
    }

    /**
     * IF
     *  - contract details are authored on the booking widget
     *  - the user is not authenticated OR the profile PRP does not match a existing program in the BW
     * THEN use the authored contract as the initialValue
     */
    if (contractDetails?.contractDetails && profileProgramDoesntMatchComponentProgram) {
      modifyInitialValues.contractNumber = contractDetails.contractDetails.contractNumber;
    }

    if (couponDetails?.couponDetails?.[0]) {
      modifyInitialValues.couponCode = couponDetails?.couponDetails?.[0]?.description;
    }

    if (productCode) {
      modifyInitialValues.productCode = productCode;
    }

    /**
     * IF
     *  - PRP data is authored on the booking widget
     *  - user is unauthenticted or profile PRP do not match the program authored to the booking widget
     * THEN add the PRP fields/data to BW initial values
     */
    if (partnerRewardProgram && profileProgramDoesntMatchComponentProgram) {
      modifyInitialValues = {
        ...modifyInitialValues,
        partnerRewardsProgram: partnerRewardProgram.name,
        partnerRewardsCode: partnerRewardProgram.code,
      };
    }

    // check for reservation prop, or abanadoned res, or only apply the initial values declared above
    if (reservationObject) {
      applyReservation(reservationObject, modifyInitialValues);
    } else if (prefilled) {
      setAbandonedRes().then((reservation) => {
        applyReservation(reservation, modifyInitialValues);
      });
    } else {
      setInitialValues(modifyInitialValues);

      if (haveLocationFromProps) {
        setLocation(pickupLocationFromProps);
        fetchDefaultAgeRentals(pickupLocationFromProps);
      }
      if (modifyInitialValues.contractNumber) {
        setContractNumber(modifyInitialValues.contractNumber);
      }
      if (modifyInitialValues.couponCode) {
        setCoupon(couponDetails?.couponDetails?.[0]?.code);
      }
      if (modifyInitialValues.partnerRewardsCode) {
        setPartnerRewardProgram(partnerRewardProgram);
      }
    }
  }, []);

  useEffect(() => {
    initLocation();
  }, [initialValuesUpdated]);

  useEffect(() => {
    if (resetInitiateResState) {
      setHasClearedSession(true);
      removeInitiateResObject();
    }

    if (hasReservationSessionData) {
      const preservedSessionData =
        !isInResBookingWidget && !isTrueModify && reservationObjectInitialValues && !reservationSessionLDTUpdated;
      applyReservation(reservationObjectInitialValues, initialValues);

      setReservationSessionLDTUpdated(!reservationSessionLDTUpdated);
      setDisplayingPreservedSessionData(preservedSessionData);
      sessionStorage.setItem(ANALYTICS.SKIP_ANALYTICS_FOR_SAVED_RESERVATION_SESSION_LDT_VALUES, true);
    }
    if (clearSessionStorage) {
      sessionStorage.removeItem(ANALYTICS.SKIP_ANALYTICS_FOR_SAVED_RESERVATION_SESSION_LDT_VALUES);
    }

    if (selectedValues && pickup_location) {
      const initialSearchByVehiclesVal = getSearchFilterValue(vehicleSearchFilters);
      setInitialSearchByVehicles(initialSearchByVehiclesVal);
    } else {
      setInitialSearchByVehicles(i18n('all_vehicles'));
    }
  }, [resetInitiateResState, hasReservationSessionData, clearSessionStorage, selectedValues, pickup_location]);

  useEffect(() => {
    if (partnerRewardProgram && profileData) {
      changedInitialValues = true;
      if (profileProgramMatchPRP(profileData, partnerRewardProgram)) {
        // - If users program is the same as the page, we unset PRP and contract fields
        delete newInitialValues.contractNumber;
        delete newInitialValues.partnerRewardsProgram;
        delete newInitialValues.partnerRewardsCode;
      } else {
        // - If there is no profile or the profile do not match the page, we set the fields
        newInitialValues.contractNumber = contractDetails?.contractDetails?.contractNumber || '';
        newInitialValues.partnerRewardsProgram = partnerRewardProgram.name;
        newInitialValues.partnerRewardsCode = partnerRewardProgram.code;
      }
    }

    if (changedInitialValues) {
      setInitialValues(newInitialValues);
      setPartnerRewardProgram(
        newInitialValues.partnerRewardProgram || {
          code: newInitialValues?.partnerRewardsCode,
          name: newInitialValues?.partnerRewardsProgram,
        }
      );
      setContractNumber(newInitialValues.contractNumber);
    }
    if (!!productCode || showProductCode) {
      toggleDiscountCollapsed(false);
    }
  }, [partnerRewardProgram, profileData, productCode, showProductCode, changedInitialValues]);

  useClickOutside(
    {
      container: clickOutsideRef,
      handler: handleClickOutside,
      listenerConditional: disableGuidedFlow,
    },
    [disableGuidedFlow]
  );

  const handleFormClick = () => {
    // The first time the booking widget receives a click and is in focus, we want to scroll it to the top with a bit of
    // room for the Title component that we _assume_ will be authored there.
    // NOTE: This is relying on the clickoutside ref.
    // NOTE2: This changed from onFocus to onClick as the focus event is triggered before other click events and this was
    // causing some unwanted side effects.
    if (!hasBeenFocused && breakpoint.isDesktop) {
      const bwScrollingPosition = clickOutsideRef.current.offsetTop - 80; // 80 is a magic number
      scrollPage(0, bwScrollingPosition);
      setHasBeenFocused(true);
    }
    handleDisabledGuidedFlowState();
  };

  const handleOnSubmit = () => {
    const props = {
      profileData,
      reservationObject,
      isTrueModify,
      isDeeplink,
      isInResBookingWidget,
    };

    if (partnerRewardProgram) {
      if (profileData) {
        if (!profileData?.partnerRewardsProgram) {
          // If authenticated user do not have a PRP attached to the profile,
          // open the modal for Fees details and add to profile option.
          showUpdateProfileWithPRPModal();
        } else if (profileData?.partnerRewardsProgram?.code !== partnerRewardProgram?.code) {
          // If authenticated user have a preferred PRP but the code does not match,
          // open the modal to choosen between updating profile or continue with existing
          showAuthConflictPRPModal();
        } else {
          // If authenticated user have a preferred PRP and it's the same, just initiate the reservation
          handleInitiate(props);
        }
      } else if (partnerRewardProgram.surcharge) {
        showFrequentTravelerRecoveryFeeModal();
      } else {
        // Initiate directly if user not authenticated and program have no surcharge
        handleInitiate(props);
      }
    } else {
      handleInitiate(props);
    }

    // Storing the coupon/contract name which will be used to hide coupon/cid fields if not selected for auth user
    if (
      uiInitiateRequestalues?.coupons ||
      uiInitiateRequestalues?.contract_name ||
      uiInitiateRequestalues?.contract_number
    ) {
      sessionStorage.setItem(
        IS_RES_INITIATED_COUPON_CID,
        JSON.stringify({
          coupons: uiInitiateRequestalues?.coupons,
          contractName: uiInitiateRequestalues?.contract_name,
          contractNumber: uiInitiateRequestalues?.contract_number,
          pathname: location.pathname,
        })
      );
    }

    setDiscountCodeExpanded(false);

    // Adds text to loading-overlay based on location selection
    const overlay = document.querySelector('.loading-overlay');
    if (overlay?.innerHTML) {
      pickup_location?.type === 'CITY' || return_location?.type === 'CITY'
        ? (overlay.innerHTML = `<p>${i18n('finding_your_locations')}</p>`)
        : (overlay.innerHTML = `<p>${i18n('loading_your_vehicles')}</p>`);
    }
  };

  /**
   * renderCollapsedSection - Renders fields that might be collapsed on initial render
   */
  const renderCollapsedSection = (submitFailed) => {
    const pickUpLocationID = pickup_location?.type === LOCATIONS.TYPE_BRANCH ? pickup_location_id : null;
    const returnLocationID = return_location?.type === LOCATIONS.TYPE_BRANCH ? return_location_id : null;
    const renderPRPFields = enablePartnerRewards && !profileProgramMatchPRP(profileData, partnerRewardProgram);
    const readOnlyContractField =
      contractDetails?.contractDetails?.allowAccountRemovalIndicator === false || isTrueModify;
    // If window minus component size is larger than 600 and the component is smaller than 1250, it means that the BW should be handled as deals page style
    const isBWOnDealsPage =
      window.innerWidth - (clickOutsideRef?.current?.offsetWidth ?? 0) > 600 &&
      clickOutsideRef?.current?.offsetWidth < 1250;

    return (
      <Fragment>
        {isOneWay && (
          <div className='fieldset fieldset--location-search'>
            <LocationSearch
              id='returnLocation'
              formId='formWrapper'
              name='returnLocation'
              type={LOCATIONS.LOCATION_SEARCH_TYPES.BOOKING_WIDGET}
              label={i18n('location_search_return_placeholder')}
              setLocation={handleSetLocation(EXCHANGE_TYPE_RETURN)}
              unsetLocation={handleUnsetLocation(EXCHANGE_TYPE_RETURN)}
              toggleOneWay={onToggleOneWay}
              isOneWay={isOneWay}
              selectedLocation={return_location}
              guided
              validate={(value) => validateReturnLocation(value, return_location)}
            />
          </div>
        )}

        <div
          className={cn('booking-widget__input-group', {
            'booking-widget__input-group--deals': isBWOnDealsPage,
          })}
        >
          <div className='booking-widget__input-group-left'>
            <LocationDateTimeFieldGroup
              pickup_location_id={pickUpLocationID || (!pickupLocationRemoved ? initialValues?.pickupLocation?.id : '')}
              return_location_id={returnLocationID || (!returnLocationRemoved ? initialValues?.returnLocation?.id : '')}
              pickupDate={pickupDate}
              pickupDateValue={pickupDateValue}
              pickupEligibility={pickupEligibility}
              pickupTime={pickupTime}
              returnDate={returnDate}
              returnDateValue={returnDateValue}
              returnEligibility={returnEligibility}
              returnTime={returnTime}
              onDateChangeReturn={handleSetDate(EXCHANGE_TYPE_RETURN)}
              onTimeChangeReturn={handleSetTime(EXCHANGE_TYPE_RETURN)}
              onDateChangePickUp={handleSetDate(EXCHANGE_TYPE_PICKUP)}
              onTimeChangePickUp={handleSetTime(EXCHANGE_TYPE_PICKUP)}
              isSixColumnGrid={isSixColumnGrid}
              guided
              contractRestrictions={
                (isLastMinuteSpecials || isPlanAheadSpecials || initialValues?.contractIsLmOrPlanAheadSpecials) &&
                !isLmOrPlanAheadDiscountRemoved &&
                (authoredFormattedContractRestrictions || initialValues?.formattedContractRestrictions)
              }
            />

            {!profileData?.age && (
              <div className='fieldset fieldset--drivers-age'>
                <DriversAge
                  readOnly={isTrueModify}
                  value={initialValues?.driversAge}
                  tooltip={isTrueModify && i18n('true_modify_booking_widget_driver_age_tooltip')}
                  isDriverAgeUpdated={isDriverAgeUpdated}
                  driversAge={initialValues?.driversAge}
                />
              </div>
            )}

            {renderPRPFields && (
              <div className='fieldset fieldset-group fieldset--partner-rewards'>
                <PartnerRewardsFields
                  setPartnerMemberNumber={setPartnerRewardProgramMemberId}
                  submitFailed={submitFailed}
                />
              </div>
            )}
            <div
              className={cn('fieldset fieldset--discount-code fieldset-group', {
                'fieldset--discount-code-coupon': !disableCouponCode,
                'fieldset--product-code-coupon': !!productCode || showProductCode,
              })}
            >
              <DiscountCodeVehicleClass
                openRemoveProductCodeModal={openRemoveProductCodeModal}
                isDiscountCollapsed={isDiscountCollapsed}
                setContract={setContractNumber}
                setCoupon={setCoupon}
                initialProductCode={productCode}
                initialCouponCode={initialValues?.couponCode}
                initialContractCode={initialValues?.contractNumber || initialValues?.contractDetails?.contract_number}
                initialContractName={initialValues?.contractName}
                initialIsLmOrPlanAheadSpecials={initialValues?.contractIsLmOrPlanAheadSpecials}
                showProductCode={showProductCode}
                onExpand={handleDiscountCodeExpand}
                onLmOrPlanAheadDiscountRemoval={handleLmOrPlanAheadDiscountRemoved}
                readOnlyContract={readOnlyContractField}
                isLmsOrPlanAhedSpecials={isLastMinuteSpecials || isPlanAheadSpecials}
                disableCouponCode={disableCouponCode}
                isSixColumnGrid={isSixColumnGrid}
                isPartnerRewards={enablePartnerRewards}
                authoredContract={contractDetails}
                isDeeplink={isDeeplink}
                isTrueModify={isTrueModify}
                breakpoint={breakpoint}
                disableVCFilter={one_way_rental}
                pickupLocationId={pickup_location_id}
                showVehicleClassFilterModal={showVehicleClassFilterModal}
                initialSearchByVehicles={initialSearchByVehicles}
                displayingPreservedSessionData={displayingPreservedSessionData}
              />
              {breakpoint.isTablet && (
                <div className='booking-widget__input-group-right'>
                  <Button className='button-go' type='submit'>
                    {i18n('bw_submit_label')}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {(!breakpoint.isTablet || isBWOnDealsPage) && (
            <div className='booking-widget__input-group-right'>
              <Button className='button-go' type='submit'>
                {i18n('bw_submit_label')}
              </Button>
            </div>
          )}
        </div>
      </Fragment>
    );
  };

  const pickupInputLabel = i18n(
    isOneWay ? 'location_search_placeholder' : 'location_search_pickup_and_return_placeholder'
  );
  // AEM is sending an empty pickup_location object so account for that
  let selectedLocation;
  if (!utils.isObjectEmpty(pickup_location)) {
    selectedLocation = pickup_location;
  }

  const returnHomeAnchor = (
    <Anchor
      href={config.getRedirectUrl(WINDOW_OBJECT_KEYS.HOME_PAGE_URL)}
      className='link link--text'
      data-dtm-track={dtm(ANALYTICS.RES_WIDGET, ANALYTICS.UI_ALERT, ANALYTICS.RETURN_HOME)}
    >
      {i18n('special_rates_not_available_banner_link_label')}
    </Anchor>
  );

  const formId = 'formWrapper';

  return (
    <div id={formId}>
      <Form
        onSubmit={handleOnSubmit}
        validate={validate}
        subscription={{ submitting: true, pristine: true, submitFailed: true }}
        initialValues={initialValues}
        mutators={{ setFieldData }}
        keepDirtyOnReinitialize
      >
        {({ form, handleSubmit, submitFailed }) => (
          <form
            onClick={handleFormClick}
            onSubmit={handleSubmit}
            className={cn('booking-widget', {
              'show-discount': !isDiscountCollapsed || discountCodesExpanded,
              'show-discount--with-coupon': !isDiscountCollapsed && !disableCouponCode,
            })}
            autoComplete='off'
            noValidate
            ref={clickOutsideRef}
          >
            <ServiceErrors statePath={GMI_SERVICE_PATHS.RESERVATIONS_INITIATE} runAllHandlers />
            {submitFailed && enablePartnerRewards && <FormErrors />}

            {(contractDetails || initialValues?.contractDetails) &&
              (isLastMinuteSpecials || isPlanAheadSpecials || initialValues?.contractIsLmOrPlanAheadSpecials) &&
              restrictedDate &&
              !isLmOrPlanAheadDiscountRemoved && (
                <GenericNotification
                  key={i18n('special_rates_not_available_banner_title')}
                  messageClass='generic-notification__notifications-on'
                  title={i18n('special_rates_not_available_banner_title')}
                  message={i18n('special_rates_not_available_banner_message', [returnHomeAnchor], {
                    jsx: true,
                  })}
                  dtmTrack={dtm(ANALYTICS.RES_WIDGET, ANALYTICS.UI_ALERT, ANALYTICS.INVALID_DATES)}
                />
              )}
            <div className={cn('fieldset fieldset--location-search', { 'round-trip': !isOneWay })}>
              <LocationSearch
                id='pickupLocation'
                formId={formId}
                name='pickupLocation'
                type={LOCATIONS.LOCATION_SEARCH_TYPES.BOOKING_WIDGET}
                label={pickupInputLabel}
                setLocation={handleSetLocation()}
                unsetLocation={handleUnsetLocation()}
                toggleOneWay={!isOneWay ? onToggleOneWay : null}
                isOneWay={isOneWay}
                selectedLocation={selectedLocation}
                attention={isCollapsed}
                isFromBW
              />
            </div>
            {!isCollapsed && renderCollapsedSection(submitFailed)}
            <LocationDateTimeFormSpy form={form} />
            <GuidedFormIndicator form={form} disableGuidedFlow={disableGuidedFlow} formRef={clickOutsideRef} />
          </form>
        )}
      </Form>
      <PrerateAdditionalInfoModal />
      <FrequentTravelerRecoveryFeeModal />
      <PRPAuthAddProgramModal />
      <PRPAuthConflictModal />
      <RemoveProductCodeModal />
      <VehicleClassFilterModal />
      <CidConflictModal />
    </div>
  );
};

export default BookingWidget;

BookingWidget.propTypes = {
  contract_number: PropTypes.string,
  coupons: PropTypes.array,
  couponDetails: PropTypes.object,
  handleInitiate: PropTypes.func.isRequired,
  one_way_rental: PropTypes.bool,
  pickup_location: PropTypes.object,
  pickup_location_id: PropTypes.string,
  pickupLocationFromProps: PropTypes.object,
  renter_age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  return_location: PropTypes.object,
  return_location_id: PropTypes.string,
  setCoupon: PropTypes.func.isRequired,
  setContractNumber: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool,
  breakpoint: PropTypes.object,
  contractDetails: PropTypes.object,
  partnerRewardProgram: PropTypes.object,
  enablePartnerRewards: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setPartnerRewardProgram: PropTypes.func,
  setPartnerRewardProgramMemberId: PropTypes.func,
  isTrueModify: PropTypes.bool,
  profileData: PropTypes.shape({
    age: PropTypes.number,
    partnerRewardsProgram: PropTypes.object,
  }),
  showFrequentTravelerRecoveryFeeModal: PropTypes.func,
  showCidConflictModal: PropTypes.func,
  showUpdateProfileWithPRPModal: PropTypes.func,
  showAuthConflictPRPModal: PropTypes.func,
  disableCouponCode: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  contractCollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  collapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  bundleProfile: PropTypes.string,
  setBundleProfile: PropTypes.func,
  showProductCode: PropTypes.bool,
  returnLocationCollapsed: PropTypes.bool,
  openRemoveProductCodeModal: PropTypes.func,
  productCode: PropTypes.string,
  rentalStateConfirmed: PropTypes.bool,
  removeInitiateResObject: PropTypes.func,
  reservationObject: PropTypes.object,
  driversAgeInitialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefilled: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setAbandonedRes: PropTypes.func,
  getAgeRangeDefaults: PropTypes.func,
  clearDate: PropTypes.func,
  pickupDate: PropTypes.string,
  returnDate: PropTypes.string,
  setDate: PropTypes.func,
  setTime: PropTypes.func,
  unsetLocation: PropTypes.func,
  pickupDateValue: PropTypes.string,
  pickupEligibility: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  pickupTime: PropTypes.string,
  returnDateValue: PropTypes.string,
  returnEligibility: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  returnTime: PropTypes.string,
  isDeeplink: PropTypes.bool,
  showVehicleClassFilterModal: PropTypes.func,
  clearInitialVehicleClassFilterData: PropTypes.func,
};

BookingWidget.defaultProps = {
  isCollapsed: false,
  pickupLocationFromProps: {},
};
